import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { SectionRail } from '@/components/common/section-rail';
import { formatDateForUrl } from '@/lib/date';
import { getImageUrl } from '@/lib/image';
import { cn } from '@/lib/utils';
// `@/types/search` also exports a `Location` with a different shape — this is the one
// that matches the `siteGroups` payload.
import { type Location as SiteLocation } from '@/types/model';
import { type Site } from '@/types/search';
import { Link } from '@inertiajs/react';

interface DestinationsSectionProps {
  sites?: Site[];
  /** The `siteGroups` prop — real Location rows, used here purely for their artwork. */
  locations?: SiteLocation[];
  className?: string;
}

/**
 * Destination rail built from the bookable sites, so every tile leads somewhere.
 *
 * Artwork comes from the site's Location (the only entity here with an admin-uploaded
 * image — BookingSiteService's select excludes the site's own `featured_image`). Sites
 * without a location fall back to a typographic tile rather than a stand-in photograph.
 *
 * Note we deliberately do NOT read `siteGroups[].sites`: that nested relation is always
 * empty, because the eager-load's select omits the `location_id` foreign key Eloquent
 * needs to match children to parents.
 */
export function DestinationsSection({ sites, locations, className }: DestinationsSectionProps) {
  const bookable = (sites ?? []).filter((site) => site.mapping_code && site.name);
  if (bookable.length === 0) return null;

  const locationsById = new Map((locations ?? []).map((location) => [location.id, location]));

  return (
    <SectionRail
      title="Điểm đến nổi bật"
      subtitle="Chọn nơi bạn muốn đi"
      viewAllHref={SiteInertiaController.search().url}
      itemClassName="w-[180px] md:w-[220px]"
      className={className}
    >
      {bookable.map((site) => (
        <DestinationTile key={site.id} site={site} location={site.location ?? locationsById.get(site.location_id ?? -1)} />
      ))}
    </SectionRail>
  );
}

function DestinationTile({ site, location }: { site: Site; location?: SiteLocation }) {
  const imageUrl = location?.image ? getImageUrl(location.image, 'location') : null;

  // `/dich-vu` redirects (and falls back to a hardcoded site) when either param is
  // missing, so always send both.
  const href = SiteInertiaController.search({
    query: { site: site.mapping_code as string, date: formatDateForUrl(new Date()) },
  }).url;

  return (
    <Link
      href={href}
      className={cn(
        'group focus-visible:ring-ring relative block aspect-square overflow-hidden rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        !imageUrl && 'border border-[color:var(--pill-border)]',
      )}
    >
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-3">
            <p className="line-clamp-2 text-sm leading-snug font-semibold text-white md:text-base">{site.name}</p>
            {location?.name && <p className="mt-0.5 truncate text-xs text-white/75">{location.name}</p>}
          </div>
        </>
      ) : (
        // No artwork — set the name as the tile rather than faking a photograph.
        <div className="flex size-full flex-col justify-between p-4" style={{ backgroundColor: 'var(--surface-muted)' }}>
          <span className="text-xs font-medium tracking-wide text-gray-400 uppercase">Điểm đến</span>
          <p className="line-clamp-3 text-base leading-snug font-bold text-gray-900 md:text-lg">{site.name}</p>
        </div>
      )}
    </Link>
  );
}
